// src/services/sessionEnd.js
//
// ONE teardown for a signed-in session (docs/bugs/idle_signout_listener_leak_BUG.md).
//
// Signing out closes every Firestore listener the session opened (sessionListeners.js) BEFORE auth goes away, so no
// listener runs into "Missing or insufficient permissions". Then the page loads fresh. The reload is the reset: no
// store keeps the last user's listeners or data, and the next sign-in starts clean. It is the same reason the
// impersonation switch reloads (ImpersonationModal.vue). Stores need no logout list, and a store added later can't
// be forgotten.
//
//   endSession()                    this tab signs out: userStore.signOutUser() (idle auto-logout, header, portal,
//                                   mobile, impersonation exit, maintenance and monitoring pages)
//   planSessionTransition() →       the auth watcher in App.vue: a session that ended some other way (another tab
//     reloadAfterSessionChange()    signed out, a revoked account), or another account that took over, closes what is
//                                   still open and loads the page fresh. Never on a signed-out visitor's page load.

import { getAuth, signOut } from 'firebase/auth'
import { closeAllListeners } from './sessionListeners'

let ending = false

/** Dispose an isolated in-memory registration context without ending the main session. */
export async function endRegistrationSession(
  { app, auth },
  {
    getAuthFn = getAuth,
    signOutFn = signOut,
    deleteAppFn = async value => {
      const { deleteApp } = await import('firebase/app')
      await deleteApp(value)
    },
  } = {}
) {
  const primary = getAuthFn()
  if (
    !app ||
    !auth ||
    auth === primary ||
    app === primary?.app ||
    auth.app !== app ||
    !/^portal-registration-[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(
      app.name || ''
    ) ||
    getAuthFn(app) !== auth
  ) {
    throw new Error('An isolated registration context is required.')
  }
  try {
    await signOutFn(auth)
  } finally {
    await deleteAppFn(app)
  }
}

/** This tab started a session end and it is under way: the auth watcher leaves the page to endSession. */
export function isSessionEnding() {
  return ending
}

/**
 * What a change of signed-in user asks for.
 *   'ended'    a user was signed in and now nobody is (whoever signed them out)
 *   'switched' a different account signed in without passing through signed out
 *   'none'     anything else: a signed-out page load, a sign-in, the same user refreshed
 */
export function planSessionTransition(oldUser, newUser) {
  if (oldUser && !newUser) return 'ended'
  if (oldUser?.uid && newUser?.uid && oldUser.uid !== newUser.uid) return 'switched'
  return 'none'
}

/**
 * End this tab's signed-in session: close every listener, run the app's own teardown (voice, in-flight fetches),
 * sign out, then load `redirectTo` fresh. Runs once. The page is loaded fresh even if signing out throws: a page
 * whose listeners are all closed must not keep running on stale data.
 *
 * @param {object} [opts]
 * @param {string} [opts.redirectTo]  where the fresh page loads (default: the signed-out page)
 * @param {() => void} [opts.teardown] the App-level teardown (App.vue's cleanupAllSubscriptions)
 */
export async function endSession({
  redirectTo = '/signed-out',
  teardown = null,
  auth = null,
  signOutFn = signOut,
  navigate = url => window.location.replace(url),
} = {}) {
  if (ending) return
  ending = true
  closeAllListeners()
  try {
    teardown?.()
  } catch (err) {
    console.warn('[sessionEnd] teardown failed:', err)
  }
  try {
    await signOutFn(auth || getAuth())
  } finally {
    navigate(redirectTo)
  }
}

/**
 * A session this tab didn't end (auth already changed underneath it), or another account took over: close whatever is
 * still open and load the page fresh. A public page reloads where it is; any other page loads the signed-out page.
 */
export function reloadAfterSessionChange({
  transition,
  onPublicRoute,
  reload = () => window.location.reload(),
  navigate = url => window.location.replace(url),
}) {
  closeAllListeners()
  if (transition === 'switched' || onPublicRoute) reload()
  else navigate('/signed-out')
}

/** Tests only: forget that a session end ran. */
export function __resetSessionEndForTests() {
  ending = false
}
